import React, { useState, useEffect, useCallback } from 'react';
import client from '../api/client';
import './AdminPanel.css';

function AdminPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('pending');
  const [actionLoading, setActionLoading] = useState(null);

  const statusLabel = { pending: 'pendiente', approved: 'aprobado' };
  const roleLabel = { admin: 'admin', user: 'usuario' };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await client.get('/api/admin/users');
      setUsers(res.data);
    } catch (err) {
      setError('Error al cargar los usuarios.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleApprove = async (uid) => {
    setActionLoading(uid + '-approve');
    try {
      await client.put(`/api/admin/users/${uid}/approve`);
      setUsers(prev => prev.map(u => u.id === uid ? { ...u, status: 'approved' } : u));
    } catch {
      setError('Error al aprobar el usuario.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (uid) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este usuario?')) return;
    setActionLoading(uid + '-delete');
    try {
      await client.delete(`/api/admin/users/${uid}`);
      setUsers(prev => prev.filter(u => u.id !== uid));
    } catch {
      setError('Error al eliminar el usuario.');
    } finally {
      setActionLoading(null);
    }
  };

  const pendingUsers = users.filter(u => u.status === 'pending');
  const displayUsers = tab === 'pending' ? pendingUsers : users;

  return (
    <div className="admin-panel">
      <h1 className="admin-title">🛡️ Panel de administración</h1>

      <div className="admin-tabs">
        <button
          className={`admin-tab ${tab === 'pending' ? 'active' : ''}`}
          onClick={() => setTab('pending')}
        >
          Usuarios pendientes
          {pendingUsers.length > 0 && (
            <span className="tab-badge">{pendingUsers.length}</span>
          )}
        </button>
        <button
          className={`admin-tab ${tab === 'all' ? 'active' : ''}`}
          onClick={() => setTab('all')}
        >
          Todos los usuarios
        </button>
      </div>

      {error && <div className="error-msg">{error}</div>}

      {loading ? (
        <div className="loading">Cargando usuarios...</div>
      ) : displayUsers.length === 0 ? (
        <div className="admin-empty">
          {tab === 'pending'
            ? '✅ ¡No hay usuarios pendientes!'
            : 'No se encontraron usuarios.'}
        </div>
      ) : (
        <div className="admin-user-list">
          {displayUsers.map(u => (
            <div key={u.id} className="admin-user-card card">
              <div className="admin-user-info">
                <div className="admin-user-name">{u.username}</div>
                <div className="admin-user-email">{u.email}</div>
                <div className="admin-user-meta">
                  <span className={`status-badge status-${u.status}`}>{statusLabel[u.status] || u.status}</span>
                  <span className={`role-badge role-${u.role}`}>{roleLabel[u.role] || u.role}</span>
                </div>
              </div>
              <div className="admin-user-actions">
                {u.status === 'pending' && (
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => handleApprove(u.id)}
                    disabled={actionLoading === u.id + '-approve'}
                  >
                    {actionLoading === u.id + '-approve' ? '…' : '✓ Aprobar'}
                  </button>
                )}
                {u.role !== 'admin' && (
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDelete(u.id)}
                    disabled={actionLoading === u.id + '-delete'}
                  >
                    {actionLoading === u.id + '-delete' ? '…' : '🗑️ Eliminar'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AdminPanel;
